import { useState } from 'react';
import { VoiceIntegrityUseCase, VoiceIntegrityInfo } from 'interfaces/twilio';
import { UserDataResult } from "interfaces/users";
import { getServicesManager } from 'services';

interface FreeCallerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: UserDataResult | null;
  voiceIntegrityInfo: VoiceIntegrityInfo | null;
}

const initialVoiceIntegrityForm = {
  use_case: VoiceIntegrityUseCase.LEAD_GENERATION,
  business_employee_count: "0",
  average_business_day_call_volume: "0",
  notes: ""
};

export function FreeCallerModal({ 
  isOpen, 
  onClose, 
  onSuccess, 
  user, 
  voiceIntegrityInfo 
}: FreeCallerModalProps) {
  const [voiceIntegrityForm, setVoiceIntegrityForm] = useState(() => {
    if (voiceIntegrityInfo?.attributes) {
      return {
        use_case: voiceIntegrityInfo.attributes.use_case,
        business_employee_count: voiceIntegrityInfo.attributes.business_employee_count,
        average_business_day_call_volume: voiceIntegrityInfo.attributes.average_business_day_call_volume,
        notes: voiceIntegrityInfo.attributes.notes
      };
    }
    return initialVoiceIntegrityForm;
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [voiceIntegritySubmitSuccess, setVoiceIntegritySubmitSuccess] = useState(false);

  if (!isOpen) return null;

  // Handle form input changes
  const handleVoiceIntegrityFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setVoiceIntegrityForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission
  const handleVoiceIntegritySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.team_id) return;

    setIsSubmitting(true);
    try {
      const result = await getServicesManager().updateVoiceIntegrityStatus(
        user.team_id,
        voiceIntegrityForm.use_case as VoiceIntegrityUseCase,
        parseInt(voiceIntegrityForm.business_employee_count),
        parseInt(voiceIntegrityForm.average_business_day_call_volume),
        voiceIntegrityForm.notes
      );

      if (result) {
        setVoiceIntegritySubmitSuccess(true);
        
        // Close the form after successful submission
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      }
    } catch (error) {
      console.error("Error submitting voice integrity data:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Free Caller Registration</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>
        
        {voiceIntegritySubmitSuccess ? (
          <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
            <p className="text-green-700 font-medium">Registration successful!</p>
            <p className="text-green-600 text-sm mt-1">Your caller information has been registered.</p>
          </div>
        ) : (
          <form onSubmit={handleVoiceIntegritySubmit} className="space-y-6">
            <div>
              <label htmlFor="use_case" className="block text-sm font-medium text-gray-700 mb-1">
                Use Case
              </label>
              <select
                id="use_case"
                name="use_case"
                value={voiceIntegrityForm.use_case}
                onChange={handleVoiceIntegrityFormChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              >
                {Object.values(VoiceIntegrityUseCase).map((useCase) => (
                  <option key={useCase} value={useCase}>
                    {useCase}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="business_employee_count" className="block text-sm font-medium text-gray-700 mb-1">
                Business Employee Count
              </label>
              <input
                type="number"
                id="business_employee_count"
                name="business_employee_count"
                value={voiceIntegrityForm.business_employee_count}
                onChange={handleVoiceIntegrityFormChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            
            <div>
              <label htmlFor="average_business_day_call_volume" className="block text-sm font-medium text-gray-700 mb-1">
                Average Business Day Call Volume
              </label>
              <input
                type="number"
                id="average_business_day_call_volume"
                name="average_business_day_call_volume"
                value={voiceIntegrityForm.average_business_day_call_volume}
                onChange={handleVoiceIntegrityFormChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                Additional Notes
              </label>
              <textarea
                id="notes"
                name="notes"
                value={voiceIntegrityForm.notes}
                onChange={handleVoiceIntegrityFormChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition duration-200 flex items-center justify-center min-w-[150px] ${isSubmitting ? 'opacity-90 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 mr-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    </div>
                    <span>Submitting...</span>
                  </div>
                ) : (
                  'Submit Registration'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}